import { fire, base, storage } from "../fire";
import { getObjectAttrValueAtIndex, getObjectAttrAtIndex } from "../helper";
import RandomString from "randomstring";
import * as _ from "lodash";
import Auth from "./Auth";

class SessionService {
  _baseEndpoint = () => `sessions/${Auth.getUserId()}`;
  _baseEndpointInstances = () => `sessionInstances`;
  //Route servant uniquement à stocker les noms des participants
  _baseEndpointParticipants = () => `sessions`;
  _storageSequencesBaseRef = () => `sequences/${Auth.getUserId()}`;
  _storageInterludesBaseRef = () => `interludes/${Auth.getUserId()}`;
  _userBaseRef = () => `users/${Auth.getUserId()}`;
  _storageConclusionsBaseRef = () => `conclusions/${Auth.getUserId()}`;
  _storageChannelCriteriaImagesBaseRef = () =>
    `channelCriterias/${Auth.getUserId()}`;

  getSessionById = (sessionId) =>
    base.fetch(`${this._baseEndpoint()}/${sessionId}`, {});

  getSessionByIdAndAuthID = (sessionId) =>
    base.fetch(`sessions/S86sotaTDUN9yS5IsgB5ngrIoj72/${sessionId}`, {});

  getSessionByIdAndUid = (uid, sessionId) =>
    base.fetch(`sessions/${uid}/${sessionId}`, {});

  getSessionByUserAndShortname = (
    userId,
    sessionShortname,
    onlyFirstElement = true
  ) => {
    const endpoint = `sessions/${userId}`;
    const ref = fire.database().ref(endpoint);

    if (!sessionShortname) sessionShortname = "";
    return new Promise((resolve, reject) => {
      ref
        .orderByChild("metadata/shortname")
        .equalTo(sessionShortname)
        .once(
          "value",
          (snapshot) => {
            if (snapshot == null) return resolve(null);
            if (onlyFirstElement)
              return resolve(getObjectAttrValueAtIndex(snapshot.val(), 0));
            else return resolve(snapshot.val(), 0);
          },
          (e) => console.error(e)
        );
    });
  };

  getSessionInstances = (sessionId) => {
    const endpoint = `${this._baseEndpointInstances()}/${sessionId}`;

    return base
      .fetch(endpoint, {
        asArray: true,
      })
      .then((instances) => instances)
      .catch((e) => {
        console.error(
          `Error when trying to fetch session (${sessionId}) instances`
        );
        return [];
      });
  };

  syncSessionInstances = (sessionId, syncStateOptions) => {
    const endpoint = `${this._baseEndpointInstances()}/${sessionId}`;

    return base.syncState(endpoint, syncStateOptions);
  };

  getPilotedSessionInstanceIdById = (sessionId) => {
    const endpoint = `${this._baseEndpointInstances()}/${sessionId}`;
    const ref = fire.database().ref(endpoint);

    return new Promise((resolve, reject) => {
      ref
        .orderByChild("type")
        .equalTo("PILOTED")
        .on("value", (snapshot) => {
          if (snapshot == null) return resolve(null);
          return resolve(getObjectAttrAtIndex(snapshot.val(), 0));
        });
    });
  };

  getAutonomousSessionInstanceIdById = (sessionId, partcipantId) => {
    const endpoint = `${this._baseEndpointInstances()}/${sessionId}/${partcipantId}`;

    return new Promise((resolve, reject) => {
      base.fetch(endpoint, {
        then: (data) => {
          if (_.isEmpty(data)) return resolve(null);
          return resolve(data);
        },
        onFailure: (e) => reject(e),
      });
    });
  };

  getMultipleScaleResourceForSequenceId = (session, sequenceId) => {
    const { data } = session;
    const { sequences = [] } = data;
    const sequence = sequences.find((s) => s.id === sequenceId);

    if (!sequence || !sequence.resources || sequence.resources.length === 0)
      return null;
    return sequence.resources[0].id;
  };

  getParticipantNames = async (sessionId, author) => {
    // Définir l'endpoint pour les participants
    const endpoint = `${this._baseEndpointParticipants()}/${author}/${sessionId}/metadata/participants`;
    // Récupérer les données de la base de données
    return await base.fetch(endpoint, {
      context: this,
      then(data) {
        console.log(data);

        // Transformer l'objet de participants en tableau de noms de participants
        const participantNames = Object.values(data);
        return participantNames;
      },
      onFailure: (error) =>
        console.error("Error fetching participant names", error),
    });
  };

  getResultsForSession = (session, instances) => {
    const { metadata } = session;
    const { type } = metadata;

    if (!instances || instances.length === 0) return null;
    if (type === "PILOTED" /** Use Constants */)
      return this.getResultsForPilotedSession(session, instances);
    // return this.getResultsForAutonomousSession(session, instances);

    if (type === "AUTONOMOUS" /** Use Constants */)
      return this.getResultsForAutonomousSession(session, instances);
  };

  getStatsResultsForSession = (session, instances) => {
    const { metadata } = session;
    const { type } = metadata;

    if (!instances || instances.length === 0) return null;
    if (type === "PILOTED" /** Use Constants */)
      // return this.getStatsResultsForPilotedSession(session, instances);
      return this.getStatsResultsForAutonomousSession(session, instances);

    if (type === "AUTONOMOUS" /** Use Constants */)
      return this.getStatsResultsForAutonomousSession(session, instances);
  };

  getResultsForPilotedSession = (session, instances) => {
    const { metadata } = session;
    const { protocol } = metadata;

    if (!instances || instances.length === 0) return null;
    if (protocol === "MS" /** Use Constants */)
      return this.exportMultipleScaleResults(
        session,
        instances[0].results,
        instances[0].participants
      );
    if (protocol === "PC" /** Use Constants */)
      return this.exportPairComparaisonResults(session, instances[0].results);
    if (protocol === "MC" /** Use Constants */)
      return this.exportMultipleChannelResults(session, instances[0].results);
    if (protocol === "CM")
      return this.exportManualChoiceResults(session, instances[0].results);
  };
  getStatsResultsForPilotedSession = (session, instances) => {
    const { metadata } = session;
    const { protocol } = metadata;

    if (!instances || instances.length === 0) return null;
    if (protocol === "MS" /** Use Constants */)
      return this.exportStatsForMultipleScaleResults(
        session,
        instances[0].results
      );
    if (protocol === "PC" /** Use Constants */)
      return this.exportStatsForPairComparaisonResults(
        session,
        instances[0].results
      );
    if (protocol === "MC" /** Use Constants */)
      return this.exportStatsForMultipleChannelResults(
        session,
        instances[0].results
      );
  };

  /**
   * In Autonomous session, there are several instance of a session
   * Because of this, there are also several results
   * We need to merge every session instance results in a single one
   */
  getResultsForAutonomousSession = (session, instances = []) => {
    const { metadata } = session;
    const { protocol } = metadata;
    const resultsMerged = {};

    let participants = [];
    if (instances.length > 0) {
      instances.forEach((instance) => {
        if (instance.participants) {
          participants = participants.concat(instance?.participants);
        }
      });
    }

    let filteredInstances = instances.filter(
      (instance) => instance.currentStep === "FEEDBACKS"
    );

    // Merge results of each session instance
    filteredInstances.map((instance) =>
      _.merge(resultsMerged, instance.results)
    );

    if (!instances || instances.length === 0) return null;
    //Multiples criterias
    if (protocol === "MS" /** Use Constants */) {
      return this.exportMultipleScaleResults(
        session,
        resultsMerged,
        participants
      );
    }
    if (protocol === "PC" /** Use Constants */)
      return this.exportPairComparaisonResults(session, resultsMerged);
    //Multiples channels
    if (protocol === "MC" /** Use Constants */) {
      return this.exportMultipleChannelResults(
        session,
        resultsMerged,
        participants
      );
    }
    if (protocol === "CM")
      return this.exportManualChoiceResults(
        session,
        resultsMerged,
        instances,
        participants
      );
  };
  getStatsResultsForAutonomousSession = (session, instances = []) => {
    const { metadata } = session;
    const { protocol } = metadata;
    const resultsMerged = {};

    // Merge results of each session instance
    instances.map((instance) => _.merge(resultsMerged, instance.results));
    if (!instances || instances.length === 0) return null;
    if (protocol === "MS" /** Use Constants */)
      return this.exportStatsForMultipleScaleResults(session, resultsMerged);
    if (protocol === "PC" /** Use Constants */)
      return this.exportStatsForPairComparaisonResults(session, resultsMerged);
    if (protocol === "MC" /** Use Constants */)
      return this.exportStatsForMultipleChannelResults(session, resultsMerged);
  };

  /**
   * Here is the structure of export.
   * The export represents a list of resource with, for each one, the list of criterias with their results.
   * (All attributes starting with a '$' are dynamic)
   * [
   *      { resource: $resourceObj, $nameOfCriteria : { total: $total, numberOfVotes: $numberOfVotes, average: $average } },
   *      { resource: $resourceObj, $nameOfCriteria : { total: $total, numberOfVotes: $numberOfVotes, average: $average } },
   *      { resource: $resourceObj, $nameOfCriteria : { total: $total, numberOfVotes: $numberOfVotes, average: $average } },
   *      ....
   * ]
   */
  //Multiple Criterias
  exportMultipleScaleResults = (session, results, participants) => {
    const { data } = session;
    const sessionId = session.id;
    const { msCriterias = [], sequencesResources = [] } = data;
    const formatedResults = [];

    /**
     * keys(results) return a list of sequence id
     * For each sequence id, we have a list of participant vote
     * For each participant vote, we have a list of criteria with the participant chosen value
     */
    _.keys(results).map((sequenceId) => {
      const sequenceFormatedResult = {};
      const sequenceResult = results[sequenceId];
      const participantsVote = _.omit(sequenceResult, "_fakeId");
      const numberOfVotes = _.keys(participantsVote).length;
      // In MultipleScale, a sequence is associated with only one resource. Let's get this resource.
      const resource = sequencesResources.find(
        (r) =>
          r.id ===
          this.getMultipleScaleResourceForSequenceId(session, sequenceId)
      );

      sequenceFormatedResult.resource = resource;
      /**
       * For each criteria, we will store :
       * - total : the total is the sum of all participant chosen value for the given criteria
       * - numberOfVotes : the number of votes which allow us to find the average value
       * - average : the average values of the given criteria according to total and numberOfVotes
       */
      msCriterias.map(
        // eslint-disable-next-line
        (criteria) => {
          if (criteria.type === "OPEN") {
            sequenceFormatedResult[criteria.name] = {
              type: "OPEN",
              numberOfVotes,
              verbatims: [],
            };
          } else {
            sequenceFormatedResult[criteria.name] = {
              type: "CLOSE",
              total: 0,
              numberOfVotes,
              average: 0,
              scores: [],
            };
          }
        }
      );

      // For each participant vote
      for (let participantId in participantsVote) {
        const vote = participantsVote[participantId];

        let participantName =
          participants.find((part) => part.id === participantId)?.infos?.name ||
          "";

        // For each criteria the participant has voted for
        for (let criteriaId in vote) {
          let criteriaName;
          //Session cofifam
          if (sessionId === "VznqJjxv4bbzoo9qgutpFZyjc5A8lZqo") {
            const edgeCaseSessionCriteria = [
              {
                default: 5,
                id: "M0NkBnOvWBVAkqtT",
                max: 10,
                min: 1,
                name: "La technique du wall wash vous paraît-elle acceptable ?",
                step: 1,
                type: "CLOSE",
              },
              {
                default: 5,
                id: "V2pugGjeBq3tG5xZ",
                max: 10,
                min: 1,
                name: "Un ciblage « soft « des oeuvres est-il souhaitable ?",
                step: 1,
                type: "CLOSE",
              },
              {
                default: 5,
                id: "zfn4cjIEEUUy9Drj",
                max: 10,
                min: 1,
                name: "Un ciblage « cadré » des œuvres est il souhaitable ?",
                step: 1,
                type: "CLOSE",
              },
            ];

            const criteria = edgeCaseSessionCriteria.find(
              (c) => c.id === criteriaId
            );
            if (!criteria) continue;
            criteriaName = edgeCaseSessionCriteria.find(
              (c) => c.id === criteriaId
            ).name;
          } else {
            const criteria = msCriterias.find((c) => c.id === criteriaId);
            if (!criteria) continue;
            criteriaName = criteria.name;
          }

          if (
            !sequenceFormatedResult[criteriaName].type ||
            sequenceFormatedResult[criteriaName].type === "CLOSE"
          ) {
            sequenceFormatedResult[criteriaName].total += vote[criteriaId];
            sequenceFormatedResult[criteriaName].scores.push({
              score: vote[criteriaId],
              participantName: participantName,
            });
            sequenceFormatedResult[criteriaName].average = _.round(
              sequenceFormatedResult[criteriaName].total /
                sequenceFormatedResult[criteriaName].numberOfVotes,
              2
            );
          } else {
            sequenceFormatedResult[criteriaName].verbatims.push({
              text: vote[criteriaId],
              participantName: participantName,
            });
          }
        }
      }
      formatedResults.push(sequenceFormatedResult);
      return null;
    });
    return formatedResults;
  };

  /**
   * Here is the structure of export.
   * The export represents the list of vote reformated for statistics uses.
   * (All attributes starting with a '$' are dynamic)
   * [
   *      { sequence: $sequenceId, participant: $participantId, $criteriaName1: $participantVote, $criteriaName2: $participantVote, $criteriaName3: $participantVote },
   *      { sequence: $sequenceId, participant: $participantId, $criteriaName1: $participantVote, $criteriaName2: $participantVote, $criteriaName3: $participantVote },
   *      { sequence: $sequenceId, participant: $participantId, $criteriaName1: $participantVote, $criteriaName2: $participantVote, $criteriaName3: $participantVote },
   *      ....
   * ]
   */
  exportStatsForMultipleScaleResults = (session, results) => {
    const { data } = session;
    const { msCriterias = [] } = data;
    const formatedResults = [];

    _.keys(results).map((sequenceId) => {
      const sequenceResult = results[sequenceId];
      const participantsVote = _.omit(sequenceResult, "_fakeId");

      // For each participant vote
      for (let participantId in participantsVote) {
        const sequenceFormatedResult = {
          sequence: sequenceId,
          participant: participantId,
        };
        const vote = participantsVote[participantId];

        msCriterias.map(
          (criteria) => (sequenceFormatedResult[criteria.name] = null)
        );
        // For each criteria the participant has voted for
        for (let criteriaId in vote) {
          const criteriaName = msCriterias.find(
            (c) => c.id === criteriaId
          ).name;

          sequenceFormatedResult[criteriaName] = vote[criteriaId];
        }
        formatedResults.push(sequenceFormatedResult);
      }
      return null;
    });
    return formatedResults;
  };

  /**
   * Here is the structure of export.
   * The export represents a list of sequence with, for each one, the list of channel with their results.
   * (All attributes starting with a '$' are dynamic)
   * [
   *      { sequence: $sequenceObj, $nameOfChannel1 : { total: $total, numberOfVotes: $numberOfVotes, average: $average }, $nameOfChannel2... },
   *      { sequence: $sequenceObj, $nameOfChannel1 : { total: $total, numberOfVotes: $numberOfVotes, average: $average }, $nameOfChannel2... },
   *      { sequence: $sequenceObj, $nameOfChannel2 : { total: $total, numberOfVotes: $numberOfVotes, average: $average }, $nameOfChannel2... },
   *      ....
   * ]
   */

  //multiples Channels
  exportMultipleChannelResults = (session, results, participants) => {
    const { data } = session;
    const { mcCriterias = [], sequences } = data;
    const formatedResults = [];

    /**
     * keys(results) return a list of sequence id
     * For each sequence id, we have a list of participant vote
     * For each participant vote, we have a list of criteria with the participant chosen value
     */
    _.keys(results).map((sequenceId) => {
      const sequenceFormatedResult = {};
      const sequenceResult = results[sequenceId];
      const participantsVote = _.omit(sequenceResult, "_fakeId");
      const numberOfVotes = _.keys(participantsVote).length;
      const sequence = sequences.find((s) => s.id === sequenceId);

      if (!sequence) return null;

      sequenceFormatedResult.sequence = sequence;
      /**
       * For each channel, we will store :
       * - total : the total is the sum of all participant chosen value for the given channel
       * - numberOfVotes : the number of votes which allow us to find the average value
       * - average : the average values of the given channel according to total and numberOfVotes
       */
      sequence.resources.map(
        (channel) =>
          (sequenceFormatedResult[channel.name] = {
            total: 0,
            numberOfVotes,
            average: 0,
          })
      );
      mcCriterias.map(
        // eslint-disable-next-line
        (criteria) => {
          if (criteria.type === "OPEN") {
            sequenceFormatedResult[criteria.name] = {
              type: "OPEN",
              numberOfVotes,
              verbatims: [],
            };
          } else {
            sequenceFormatedResult[criteria.name] = {
              type: "CLOSE",
              total: 0,
              numberOfVotes,
              average: 0,
              scores: [],
            };
          }
        }
      );
      mcCriterias.map(
        // eslint-disable-next-line
        (criteria) => {
          if (criteria.type === "OPEN") {
            sequenceFormatedResult[criteria.name] = {
              type: "OPEN",
              numberOfVotes,
              verbatims: [],
            };
          } else {
            sequenceFormatedResult[criteria.name] = {
              type: "CLOSE",
              total: 0,
              numberOfVotes,
              average: 0,
              scores: [],
            };
          }
        }
      );

      // For each participant vote
      for (let participantId in participantsVote) {
        const vote = participantsVote[participantId];
        let participantName =
          participants.find((part) => part.id === participantId)?.infos?.name ||
          "";

        // For each channel the participant has voted for
        for (let channelId in vote) {
          const channelName = mcCriterias.find((c) => c.id === channelId).name;
          if (sequenceFormatedResult[channelName]) {
            sequenceFormatedResult[channelName].total += vote[channelId];
            sequenceFormatedResult[channelName].scores.push({
              score: vote[channelId],
              participantName: participantName,
            });
            sequenceFormatedResult[channelName].average = _.round(
              sequenceFormatedResult[channelName].total /
                sequenceFormatedResult[channelName].numberOfVotes,
              2
            );
          }
        }
      }
      formatedResults.push(sequenceFormatedResult);
      return null;
    });

    return formatedResults;
  };

  exportManualChoiceResults = (session, results, instances, participants) => {
    const { data } = session;
    const { sequences = [] } = data;
    const formatedResults = [];
    let codifabSessionId = "qYaO9UcclrH0U2zt9xHNuHBVwB5WeAvA";
    if (session.id === codifabSessionId) {
      // For each sequence
      sequences.forEach((sequence, i) => {
        let formatedResult = [];
        // Set initial score to 0
        sequence.resources.forEach((resource) => {
          formatedResult.push({
            resource,
            points: 0,
            personalData: {
              ageRange: {
                l20: 0,
                f20t29: 0,
                f30t39: 0,
                f40t49: 0,
                f50t59: 0,
                f60t69: 0,
                m70: 0,
                total: 0,
              },
              nbHoursHome: {
                l2: 0,
                f2t4: 0,
                f4t6: 0,
                f6t8: 0,
                m8: 0,
                total: 0,
              },
              nbHoursHomeCovid: {
                l2: 0,
                f2t4: 0,
                f4t6: 0,
                f6t8: 0,
                m8: 0,
                total: 0,
              },
            },
          });
        });
        // answers = { ['< 2h', "2h-4h", "4h-6h", "6h-8h", "> 8h"]}
        const sequenceVotes = _.omit(results[sequence.id], "_fakeId");
        // For each vote
        for (var participantId in sequenceVotes) {
          const vote = sequenceVotes[participantId];
          if (vote !== "null") {
            let myInstance = instances.find(
              // eslint-disable-next-line
              (instance) =>
                Object.keys(instance.results.J5hifA5WPvgDna7K)[1] ===
                  participantId ||
                Object.keys(instance.results.J5hifA5WPvgDna7K)[0] ===
                  participantId
            );

            // The participant has voted for a specific resource, let's increment the points of this resource in formated results
            const formated = formatedResult.find(
              (result) => result.resource.id === vote
            );
            // formatedResult.find(result => result.resource.id === vote).points += 1
            formated.points = formated.points + 1;

            if (myInstance && myInstance.personalQuestions) {
              if (myInstance.personalQuestions.ageRange) {
                switch (myInstance.personalQuestions.ageRange) {
                  case "< 20":
                    formated.personalData.ageRange.l20 =
                      formated.personalData.ageRange.l20 + 1;
                    formated.personalData.ageRange.total =
                      formated.personalData.ageRange.total + 1;
                    break;
                  case "20-29":
                    formated.personalData.ageRange.f20t29 =
                      formated.personalData.ageRange.f20t29 + 1;
                    formated.personalData.ageRange.total =
                      formated.personalData.ageRange.total + 1;
                    break;
                  case "30-39":
                    formated.personalData.ageRange.f30t39 =
                      formated.personalData.ageRange.f30t39 + 1;
                    formated.personalData.ageRange.total =
                      formated.personalData.ageRange.total + 1;
                    break;
                  case "40-49":
                    formated.personalData.ageRange.f40t49 =
                      formated.personalData.ageRange.f40t49 + 1;
                    formated.personalData.ageRange.total =
                      formated.personalData.ageRange.total + 1;
                    break;
                  case "50-59":
                    formated.personalData.ageRange.f50t59 =
                      formated.personalData.ageRange.f50t59 + 1;
                    formated.personalData.ageRange.total =
                      formated.personalData.ageRange.total + 1;
                    break;
                  case "60-69":
                    formated.personalData.ageRange.f60t69 =
                      formated.personalData.ageRange.f60t69 + 1;
                    formated.personalData.ageRange.total =
                      formated.personalData.ageRange.total + 1;
                    break;
                  default:
                    console.log("no results");
                }
              }
              // answers = { ['< 2h', "2h-4h", "4h-6h", "6h-8h", "> 8h"]}

              if (myInstance.personalQuestions.nbHoursHome) {
                switch (myInstance.personalQuestions.nbHoursHome) {
                  case "< 2h":
                    formated.personalData.nbHoursHome.l2 =
                      formated.personalData.nbHoursHome.l2 + 1;
                    formated.personalData.nbHoursHome.total =
                      formated.personalData.nbHoursHome.total + 1;
                    break;
                  case "2h-4h":
                    formated.personalData.nbHoursHome.f2t4 =
                      formated.personalData.nbHoursHome.f2t4 + 1;
                    formated.personalData.nbHoursHome.total =
                      formated.personalData.nbHoursHome.total + 1;

                    break;
                  case "4h-6h":
                    formated.personalData.nbHoursHome.f4t6 =
                      formated.personalData.nbHoursHome.f4t6 + 1;
                    formated.personalData.nbHoursHome.total =
                      formated.personalData.nbHoursHome.total + 1;
                    break;
                  case "6h-8h":
                    formated.personalData.nbHoursHome.f6t8 =
                      formated.personalData.nbHoursHome.f6t8 + 1;
                    formated.personalData.nbHoursHome.total =
                      formated.personalData.nbHoursHome.total + 1;

                    break;
                  case "> 8h":
                    formated.personalData.nbHoursHome.m8 =
                      formated.personalData.nbHoursHome.m8 + 1;
                    formated.personalData.nbHoursHome.total =
                      formated.personalData.nbHoursHome.total + 1;

                    break;
                  default:
                    console.log(myInstance.personalQuestions.nbHoursHome);
                }
              }

              if (myInstance.personalQuestions.nbHoursHomeCovid) {
                switch (myInstance.personalQuestions.nbHoursHomeCovid) {
                  case "< 2h":
                    formated.personalData.nbHoursHomeCovid.l2 =
                      formated.personalData.nbHoursHomeCovid.l2 + 1;
                    formated.personalData.nbHoursHomeCovid.total =
                      formated.personalData.nbHoursHomeCovid.total + 1;
                    break;
                  case "2h-4h":
                    formated.personalData.nbHoursHomeCovid.f2t4 =
                      formated.personalData.nbHoursHomeCovid.f2t4 + 1;
                    formated.personalData.nbHoursHomeCovid.total =
                      formated.personalData.nbHoursHomeCovid.total + 1;
                    break;
                  case "4h-6h":
                    formated.personalData.nbHoursHomeCovid.f4t6 =
                      formated.personalData.nbHoursHomeCovid.f4t6 + 1;
                    formated.personalData.nbHoursHomeCovid.total =
                      formated.personalData.nbHoursHomeCovid.total + 1;
                    break;
                  case "6h-8h":
                    formated.personalData.nbHoursHomeCovid.f6t8 =
                      formated.personalData.nbHoursHomeCovid.f6t8 + 1;
                    formated.personalData.nbHoursHomeCovid.total =
                      formated.personalData.nbHoursHomeCovid.total + 1;
                    break;
                  case "> 8h":
                    formated.personalData.nbHoursHomeCovid.m8 =
                      formated.personalData.nbHoursHomeCovid.m8 + 1;
                    formated.personalData.nbHoursHomeCovid.total =
                      formated.personalData.nbHoursHomeCovid.total + 1;
                    break;
                  default:
                    console.log(myInstance.personalQuestions.nbHoursHome);
                }
              }
            }
          }
        }
        let sequ = {
          results: formatedResult,
          sequenceId: sequence.id,
          sequenceContext: sequence.sequenceContext,
        };
        formatedResults.push(sequ);
      });
      return formatedResults;
    } else {
      let votersAmount = 0;
      sequences.forEach((sequence, i) => {
        let formatedResult = [];
        // Set initial score to 0
        sequence.resources.forEach((resource) => {
          formatedResult.push({ resource, points: 0, voters: [] });
        });
        const sequenceVotes = _.omit(results[sequence.id], "_fakeId");
        // For each vote
        //changement de var par let
        for (let participantId in sequenceVotes) {
          const vote = sequenceVotes[participantId];
          if (vote !== "null") {
            const formated = formatedResult.find(
              (result) => result.resource.id === vote
            );
            formated.points = formated.points + 1;
            const participant = participants.find(
              (participant) => String(participant.id) === String(participantId)
            );
            if (participant && participant.infos) {
              formated.voters.push(participant.infos.name);
            }
            if (i === 0) {
              votersAmount = votersAmount + 1;
            }
          }
        }

        let sequ = {
          results: formatedResult,
          sequenceId: sequence.id,
          sequenceContext: sequence.sequenceContext,
          votersAmount: votersAmount,
        };
        formatedResults.push(sequ);
      });
      return formatedResults;
    }
  };

  /**
   * Here is the structure of export.
   * The export represents the list of vote reformated for statistics uses.
   * (All attributes starting with a '$' are dynamic)
   * [
   *      { sequence: $sequenceId, participant: $participantId, $channelName1: $participantVote, $channelName2: $participantVote, $channelName3: $participantVote },
   *      { sequence: $sequenceId, participant: $participantId, $channelName1: $participantVote, $channelName2: $participantVote, $channelName3: $participantVote },
   *      { sequence: $sequenceId, participant: $participantId, $channelName1: $participantVote, $channelName2: $participantVote, $channelName3: $participantVote },
   *      ....
   * ]
   */
  exportStatsForMultipleChannelResults = (session, results) => {
    const { data } = session;
    const { mcCriterias = [] } = data;
    const formatedResults = [];

    _.keys(results).map((sequenceId) => {
      const sequenceResult = results[sequenceId];
      const participantsVote = _.omit(sequenceResult, "_fakeId");

      // For each participant vote
      for (let participantId in participantsVote) {
        const sequenceFormatedResult = {
          sequence: sequenceId,
          participant: participantId,
        };
        const vote = participantsVote[participantId];

        mcCriterias.map(
          (channel) => (sequenceFormatedResult[channel.name] = null)
        );
        // For each channel the participant has voted for
        for (let channelId in vote) {
          const channelName = mcCriterias.find((c) => c.id === channelId).name;

          sequenceFormatedResult[channelName] = vote[channelId];
        }
        formatedResults.push(sequenceFormatedResult);
      }
      return null;
    });
    return formatedResults;
  };

  /**
   * Here is the structure of export.
   * The export represents a list of resource with, for each one, the total of points.
   * How the total of points is calculated ?
   *  Winner : 1 pt
   *  Looser : 0 pt
   *  Equality : 0.5 pt
   * (All attributes starting with a '$' are dynamic)
   * [
   *      { resource: $resourceObj, points: $points },
   *      { resource: $resourceObj, points: $points },
   *      { resource: $resourceObj, points: $points },
   *      ....
   * ]
   */
  exportPairComparaisonResults = (session, results) => {
    const { data } = session;
    const { sequences = [], sequencesResources = [] } = data;
    const formatedResults = [];

    // Initilize formated results with each resource and default amount of point
    sequencesResources.map((resource) =>
      formatedResults.push({ resource, points: 0 })
    );
    // For each sequence
    for (var sequenceId in results) {
      const sequenceVotes = _.omit(results[sequenceId], "_fakeId");
      // eslint-disable-next-line
      const sequence = sequences.find((s) => s.id === sequenceId);

      // For each vote
      for (var participantId in sequenceVotes) {
        const vote = sequenceVotes[participantId];
        if (vote !== "null")
          // The participant has voted for a specific resource, let's increment the points of this resource in formated results
          formatedResults.find(
            (result) => result.resource.id === vote
          ).points += 2;
        else {
          // The participant hasn't voted for a particular resource, so we have to increment both challenged resources for the given sequence
          const { resources } = sequence;

          resources.forEach((r) => {
            formatedResults.find(
              (result) => result.resource.id === r.id
            ).points += 1;
          });
        }
      }
    }
    return formatedResults;
  };

  /**
   * Here is the structure of export.
   * The export represents the list of vote reformated for statistics uses.
   * (All attributes starting with a '$' are dynamic)
   * [
   *      { sequence: $sequenceId, A: $idOfResourceA, B: $idOfResourceB, participant: $idOfParticipant, V: $participantVote },
   *      { sequence: $sequenceId, A: $idOfResourceA, B: $idOfResourceB, participant: $idOfParticipant, V: $participantVote },
   *      { sequence: $sequenceId, A: $idOfResourceA, B: $idOfResourceB, participant: $idOfParticipant, V: $participantVote },
   *      ...
   * ]
   */
  exportStatsForPairComparaisonResults = (session, results) => {
    const { data, metadata } = session;
    const { sequences = [] } = data;
    const formatedResults = [];

    const participantNames = Object.values(metadata.participants).map(
      (obj) => obj.participantName
    );

    let i = 1;

    // For each sequence
    for (var sequenceId in results) {
      const sequenceVotes = _.omit(results[sequenceId], "_fakeId");
      // eslint-disable-next-line
      const sequence = sequences.find((s) => s.id === sequenceId);

      let participantIndex = 0;
      for (var participantId in sequenceVotes) {
        const vote = sequenceVotes[participantId];

        formatedResults.push({
          Participant_ID: participantId,
          Participant_Nickname: participantNames[participantIndex] || "Unknown",
          Sequence: i,
          A: sequence.resources[0].name,
          B: sequence.resources[1].name,
          Vote:
            vote === sequence.resources[0].id
              ? sequence.resources[0].name
              : sequence.resources[1].name,
        });
        participantIndex++;
      }
      i++;
    }

    return formatedResults;
  };

  getFeedbackAnswersForSession = (session, instances) => {
    const { metadata } = session;
    const { type } = metadata;

    if (!instances || instances.length === 0) return null;
    if (type === "PILOTED" /** Use Constants */)
      return this.getFeedbackAnswersForPilotedSession(session, instances);
    if (type === "AUTONOMOUS" /** Use Constants */)
      return this.getFeedbackAnswersForAutonomousSession(session, instances);
  };

  getFeedbackAnswersForPilotedSession = (session, instances) => {
    if (!instances || instances.length === 0) return null;
    return this.exportFeedbackAnswers(
      session,
      instances[0].feedbackAnswers,
      instances[0].participants
    );
  };

  /**
   * In Autonomous session, there are several instance of a session
   * Because of this, there are also several feedback answers
   * We need to merge every session instance feedback answers in a single one
   */
  getFeedbackAnswersForAutonomousSession = (session, instances = []) => {
    const resultsMerged = {};

    let participants = [];
    if (instances.length > 0) {
      instances.forEach((instance) => {
        if (instance.participants) {
          participants = participants.concat(instance?.participants);
        }
      });
    }

    // Merge feedback answers of each session instance
    instances.map((instance) =>
      instance.feedbackAnswers
        ? _.merge(resultsMerged, instance.feedbackAnswers)
        : null
    );
    if (!instances || instances.length === 0) return null;
    return this.exportFeedbackAnswers(session, resultsMerged, participants);
  };

  /**
   * Here is the structure of export.
   * The export represents a list of feedback with, for each one, a list of user with their answer
   * The average attribut represents is the average of answer in the case of a CLOSE feedback
   * (All attributes starting with a '$' are dynamic)
   * [
   *      { feedback: $feedbackObj, answers : [ { $participantId: $answer }, ... ], average: $average },
   *      { feedback: $feedbackObj, answers : [ { $participantId: $answer }, ... ], average: $average },
   *      { feedback: $feedbackObj, answers : [ { $participantId: $answer }, ... ], average: $average },
   *      ....
   * ]
   */
  exportFeedbackAnswers = (session, answers, participants) => {
    const { data } = session;
    const { feedbacks = [] } = data;
    const formatedResults = [];

    if (!answers) return formatedResults;
    feedbacks.map((feedback) =>
      formatedResults.push({ feedback, answers: [], total: 0, average: 0 })
    );

    // For each participant feedbacks
    for (let participantId in answers) {
      const participantFeedbacks = answers[participantId];

      let participantName =
        participants?.find((part) => part.id === participantId)?.infos?.name ||
        null;
      // For each feedback
      for (let feedbackId in participantFeedbacks) {
        const answer = participantFeedbacks[feedbackId];
        const feedbackData = feedbacks.find((f) => f.id === feedbackId);

        // We add the answer to the list of answers
        if (
          formatedResults.find((result) => result.feedback.id === feedbackId)
        ) {
          formatedResults
            .find((result) => result.feedback.id === feedbackId)
            .answers.push({ participantId, answer, participantName });
          // For closed feedback, we compute some calculation because the vote is done with number
          if (feedbackData.type === "CLOSE" /** Use constants */) {
            const totalOfVotes = formatedResults.find(
              (result) => result.feedback.id === feedbackId
            ).answers.length;

            formatedResults.find(
              (result) => result.feedback.id === feedbackId
            ).total += answer;
            formatedResults.find(
              (result) => result.feedback.id === feedbackId
            ).average = _.round(
              formatedResults.find(
                (result) => result.feedback.id === feedbackId
              ).total / totalOfVotes,
              2
            );
          }
        }
      }
    }
    return formatedResults;
  };

  createPilotedSessionInstance = (sessionId, instanceData) => {
    const endpoint = `${this._baseEndpointInstances()}/${sessionId}`;

    return new Promise((resolve, reject) => {
      const firebaseKey = base
        .push(endpoint, {
          data: { sessionId, ...instanceData },
        })
        .then(() => resolve(firebaseKey));
    });
  };

  createAutonomousSessionInstance = (
    sessionId,
    participantId,
    instanceData
  ) => {
    const endpoint = `${this._baseEndpointInstances()}/${sessionId}/${participantId}`;

    return base.post(endpoint, {
      data: { sessionId, ...instanceData },
    });
  };

  hasInstances = (sessionId) =>
    this.getSessionInstances(sessionId).then(
      (instances = []) => instances.length !== 0
    );

  createSession = (sessionData) => {
    const id = RandomString.generate(32);
    const endpoint = `${this._baseEndpoint()}/${id}`;
    const { metadata, data } = sessionData;
    const firebaseData = { id, metadata, data };

    return new Promise((resolve, reject) => {
      base.post(endpoint, {
        data: firebaseData,
        then: (e) => (e ? reject(e) : resolve({ firebaseData, endpoint })),
      });
    });
  };

  addResource = (data) => {
    const {
      sessionId,
      resource,
      onProgress = () => {},
      onError = () => {},
      onComplete = () => {},
    } = data;
    const storageRef = storage.ref(
      `${this._storageSequencesBaseRef()}/${sessionId}/${resource.file}`
    );
    const uploadTask = storageRef.put(resource.fileBlob, {
      cacheControl: "max-age=86400",
    });

    uploadTask.on("state_changed", onProgress, onError, onComplete);
    return storageRef;
  };

  // Méthode pour être appelée par ParticipantService et mettre à jour la base de donnée
  // avec le nom du participant
  handleNewParticipant = (
    participantName,
    participantId,
    sessionId,
    author
  ) => {
    // Définir l'endpoint pour le participant
    const endpoint = `${this._baseEndpointParticipants()}/${author}/${sessionId}/metadata/participants`;

    // Créer les données à envoyer à la base de données
    const data = { participantName };

    // Envoyer les données à la base de données
    return base.push(endpoint, {
      data: data,
    });
  };

  updateSequencesResources = (sessionId, sequencesResources) => {
    const endpoint = `${this._baseEndpoint()}/${sessionId}/data`;

    return base.update(endpoint, {
      data: { sequencesResources },
    });
  };

  addInterlude = (data) => {
    const {
      sessionId,
      interlude,
      onProgress = () => {},
      onError = () => {},
      onComplete = () => {},
    } = data;
    const storageRef = storage.ref(
      `${this._storageInterludesBaseRef()}/${sessionId}/${
        interlude.fileInternalName
      }`
    );
    const uploadTask = storageRef.put(interlude.fileBlob);

    uploadTask.on("state_changed", onProgress, onError, onComplete);
    return storageRef;
  };

  updateInterludes = (sessionId, interludes) => {
    const endpoint = `${this._baseEndpoint()}/${sessionId}/data`;

    return base.update(endpoint, {
      data: { interludes },
    });
  };

  addConclusion = (data) => {
    const {
      sessionId,
      conclusion,
      onProgress = () => {},
      onError = () => {},
      onComplete = () => {},
    } = data;
    const storageRef = storage.ref(
      `${this._storageConclusionsBaseRef()}/${sessionId}/${
        conclusion.fileInternalName
      }`
    );
    const uploadTask = storageRef.put(conclusion.fileBlob);

    uploadTask.on("state_changed", onProgress, onError, onComplete);
    return storageRef;
  };

  updateConclusions = (sessionId, conclusions) => {
    const endpoint = `${this._baseEndpoint()}/${sessionId}/data`;

    return base.update(endpoint, {
      data: { conclusions },
    });
  };

  addCustomAttribute = (attributes) => {
    const endpoint = `${this._userBaseRef()}/attributes`;

    return base.update(endpoint, { data: attributes });
  };

  addChannelCriteriaImage = (data) => {
    const {
      sessionId,
      newCriteria,
      onProgress = () => {},
      onError = () => {},
      onComplete = () => {},
    } = data;
    const storageRef = storage.ref(
      `${this._storageChannelCriteriaImagesBaseRef()}/${sessionId}/${
        newCriteria.file
      }`
    );
    const uploadTask = storageRef.put(newCriteria.fileBlob);

    uploadTask.on("state_changed", onProgress, onError, onComplete);
    return storageRef;
  };

  updateChannelCriterias = (sessionId, mcCriterias = []) => {
    const endpoint = `${this._baseEndpoint()}/${sessionId}/data`;

    return base.update(endpoint, {
      data: { mcCriterias },
    });
  };

  //stocker la session chainé dans les métadata de la session
  addChainedSession = (data) => {
    const {
      sessionId,
      chainedSession,
      onError = () => {},
      onComplete = () => {},
    } = data;

    // Ajoutez Auth.getUserId() à la référence
    const dbRef = fire
      .database()
      .ref(`sessions/${Auth.getUserId()}/${sessionId}/metadata/chainedSession`);

    // Utilisez la méthode set pour stocker linkedSession à cet emplacement
    dbRef
      .set(chainedSession) // Utilisez linkedSession directement
      .then(() => {
        console.log(
          "Chained Session stored in Firebase Database successfully."
        );
        onComplete();
      })
      .catch((error) => {
        console.error(
          "Error storing Chained Session in Firebase Database: ",
          error
        );
        onError(error);
      });
  };

  //récupérer la session chainé des metadata de la session
  getChainedSession = (data) => {
    const { sessionId, onError = () => {}, onComplete = () => {} } = data;

    // Ajoutez Auth.getUserId() à la référence
    const dbRef = fire
      .database()
      .ref(`sessions/${Auth.getUserId()}/${sessionId}/metadata/chainedSession`);

    // Utilisez la méthode get pour récupérer la chainedSession à cet emplacement
    dbRef
      .get()
      .then((snapshot) => {
        if (snapshot.exists()) {
          console.log(
            "Chained Session retrieved from Firebase Database successfully."
          );
          onComplete(snapshot.val());
        } else {
          console.log("No Chained Session available.");
          onComplete(null);
        }
      })
      .catch((error) => {
        console.error(
          "Error retrieving Chained Session from Firebase Database: ",
          error
        );
        onError(error);
      });
  };
  //Supprimer une session chainé des metadata de la session
  deleteChainedSession = (data) => {
    const { sessionId, onError = () => {}, onComplete = () => {} } = data;

    // Ajoutez Auth.getUserId() à la référence
    const dbRef = fire
      .database()
      .ref(`sessions/${Auth.getUserId()}/${sessionId}/metadata/chainedSession`);

    // Utilisez la méthode remove pour supprimer la session liée à cet emplacement
    dbRef
      .remove() // Supprime la session liée
      .then(() => {
        console.log(
          "Chained Session removed from Firebase Database successfully."
        );
        onComplete();
      })
      .catch((error) => {
        console.error(
          "Error removing Chained Session from Firebase Database: ",
          error
        );
        onError(error);
      });
  };

  deleteInstances = (session) => {
    const endpoint = `${this._baseEndpointInstances()}/${session.id}`;

    return base.remove(endpoint);
  };

  deleteSequencesInStorage = (session) => {
    const { id, data = {} } = session;
    const { sequencesResources = [] } = data;
    // eslint-disable-next-line
    const promises = sequencesResources.map((r) => {
      if (storage.ref(`${this._storageSequencesBaseRef()}/${id}/${r.file}`)) {
        storage
          .ref(`${this._storageSequencesBaseRef()}/${id}/${r.file}`)
          .delete();
      } else if (
        storage.ref(`${this._storageSequencesBaseRef()}/undefined/${r.file}`)
      ) {
        storage
          .ref(`${this._storageSequencesBaseRef()}/undefined/${r.file}`)
          .delete();
      }
    });

    return Promise.all(promises);
  };
  deleteInterludesInStorage = (session) => {
    const { id, data = {} } = session;
    const { interludes = [] } = data;
    // eslint-disable-next-line
    const promises = interludes.map((i) => {
      if (
        storage.ref(
          `${this._storageInterludesBaseRef()}/${id}/${i.fileInternalName}`
        )
      ) {
        storage
          .ref(
            `${this._storageInterludesBaseRef()}/${id}/${i.fileInternalName}`
          )
          .delete();
      } else if (
        storage.ref(
          `${this._storageInterludesBaseRef()}/undefined/${i.fileInternalName}`
        )
      ) {
        storage
          .ref(
            `${this._storageInterludesBaseRef()}/undefined/${
              i.fileInternalName
            }`
          )
          .delete();
      }
    });

    return Promise.all(promises);
  };
  deleteChannelsInStorage = (session) => {
    const { id, data = {} } = session;
    const { mcCriterias = [] } = data;
    // eslint-disable-next-line
    const promises = mcCriterias.map((c) => {
      if (
        storage.ref(
          `${this._storageChannelCriteriaImagesBaseRef()}/${id}/${c.file}`
        )
      ) {
        storage
          .ref(`${this._storageChannelCriteriaImagesBaseRef()}/${id}/${c.file}`)
          .delete();
      } else if (
        storage.ref(
          `${this._storageChannelCriteriaImagesBaseRef()}/undefined/${c.file}`
        )
      ) {
        storage
          .ref(
            `${this._storageChannelCriteriaImagesBaseRef()}/undefined/${c.file}`
          )
          .delete();
      }
    });

    return Promise.all(promises);
  };

  resetCurrentParticipantsFromPilotedSession = (session) => {
    console.log(session);
    const endpoint = `${this._baseEndpointInstances()}/${session.id}`;

    console.log(
      "🚀 ~ file: Session.js:1083 ~ SessionService ~ endpoint:",
      endpoint
    );
  };
}

export default new SessionService();
